const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

interface IToastOptions {
  duration: number;
  content: string;
  dismissable: boolean;
  className: string;
  preventDuplicates: boolean;
  onClick: () => void;
  onDismiss: () => void;
}

/**
 * @class Toast
 * @typedef  {Object} IToastOptions
 * @property {number} duration - Duration of the toast. Use `0` to prevent the toast from hiding after a certain time.
 * @property {string} content - Toast content
 * @property {boolean} dismissable - Toggle dismiss button
 * @property {string} className - Toast type. Available default options: `danger`, `warning`, `success`, `info`
 * @property {boolean} preventDuplicates - Prevents the toast from being shown if there is already a toast with the same message
 * @property {() => void} onClick - Gets called when the toast has been clicked
 * @property {() => void} onDismiss - Gets called when the toast has been dismissed by the user
 */
export default class Toast {
  duration: number;
  content: string;
  dismissable: boolean;
  className: string;
  preventDuplicates?: boolean;
  onClick: () => void;
  onDismiss: () => void;
  toastElement: JQuery<HTMLElement>;

  /**
   * Creates a Toast element in the #toasts-container
   *
   * @param  {IToastOptions?} options - Optional options.
   * @return {void}
   *
   * @example
   * const myToast = new Toast({
   *   duration: 0,
   *   content: '<strong>Something went wrong!</strong> But that\'s not your fault.',
   *   dismissable: true,
   *   className: 'danger',
   *   preventDuplicates: false,
   *   onClick: () => console.log('clicked'),
   *   onDismiss: () => console.log('dismissed')
   * });
   * myToast.show();
   */
  constructor(options: Partial<IToastOptions> = {}) {
    this.duration = options.duration >= 0 ? options.duration : 10000;
    this.content = options.content || '';
    this.dismissable = options.dismissable !== undefined ? options.dismissable : true;
    this.className = options.className || 'info';
    this.preventDuplicates = options.preventDuplicates || false;
    this.onClick = options.onClick || $.noop();
    this.onDismiss = options.onDismiss || $.noop();

    this.dismiss = this.dismiss.bind(this);
    this.hide = this.hide.bind(this);

    const elementContent = `
      <div class="toast-item ${this.className} fade hide">
        <span class="toast-item-content">
          ${this.content}
        </span>
        ${this.dismissable ? '<i class="icon-cross close-toast"></i>' : ''}
      </div>
    `;

    const existingElements = $('#toasts-container')
      .find(`.toast-item-content:contains('${this.content}')`)
      .parents();

    this.toastElement =
      (this.preventDuplicates && existingElements.length && existingElements.first()) ||
      $(elementContent).prependTo('#toasts-container');

    if (this.dismissable)
      $(this.toastElement)
        .find('.close-toast')
        .click(() => this.dismiss());

    $(this.toastElement).click(this.onClick);
  }

  /**
   * Shows the toast
   *
   * @return {void}
   */
  async show(): Promise<void> {
    this.toastElement.removeClass('hide');
    await sleep(500);
    this.toastElement.addClass('show');

    if (this.duration) setTimeout(() => this.hide(), this.duration);
  }

  /**
   * @private Internal dismiss function
   *
   * @return {void}
   */
  dismiss(): void {
    this.hide();
    this.onDismiss();
  }

  /**
   * Hides (and destroys) the toast
   *
   * @return {void}
   */
  async hide(): Promise<void> {
    this.toastElement.removeClass('show');
    await sleep(500);
    this.toastElement.addClass('hide').remove();
  }
}
